import { LoginProviderByFirebaseProviderId } from '@/enums/login';

import {
	EmailAuthProvider,
	getAuth,
	OAuthProvider,
	reauthenticateWithCredential,
	reauthenticateWithPopup,
	signInWithCustomToken,
	User,
} from 'firebase/auth';
import Cookies from 'js-cookie';
import { headerfulCachedRequest } from '@/helpers/request-helper';

export const cookieSharedDomainConf = {
	domain: process.env.NODE_ENV === 'development' ? '.localhost' : `.${JW_CONFIG.DOMAIN}`,
	path: '/',
};

export function removeAccessTokenFromCookies() {
	// this to make sure to remove old access_token cookies (without the domain and path).
	Cookies.remove('access_token');
	Cookies.remove('access_token', {
		...cookieSharedDomainConf,
	});
}

export function saveAccessTokenToCookies(token: string, expires: number | Date = 365) {
	removeAccessTokenFromCookies();
	Cookies.set('access_token', token, {
		expires,
		secure: true,
		sameSite: 'lax',
		...cookieSharedDomainConf,
	});
}

export function getAuthTokenFromCookies(): string | undefined {
	return Cookies.get('access_token');
}

export async function reauthenticateSocialAuth(provider: string): Promise<boolean> {
	try {
		const user = await currentUser();
		if (!user) return false;
		await reauthenticateWithPopup(user, new OAuthProvider(LoginProviderByFirebaseProviderId[provider]));
		return true;
	} catch (error) {
		return false;
	}
}

export function currentUser(): Promise<any> {
	return new Promise((resolve, reject) => {
		const auth = getAuth();
		if (auth.currentUser) {
			resolve(auth.currentUser);
		}
		const unsubscribe = auth.onAuthStateChanged((user: any) => {
			unsubscribe();
			resolve(user);
		}, reject);
	});
}

export async function getUserCustomToken(accessToken: string, jwId: string): Promise<User | undefined> {
	const mutation = `mutation Mutation($input: CustomTokenFromBearerTokenInput!) {
	  customTokenFromBearerToken(input: $input) {
	    customToken
	  }
	}`;
	const bearerToken = `Bearer ${accessToken}`;
	const variables = {
		input: {
			bearerToken,
		},
	};

	let response;
	try {
		response = await headerfulCachedRequest(mutation, variables, jwId);
	} catch (error: any) {
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry(
			'[user-helper.ts] Error from headerfulCachedRequest response',
			{ error, where: 'user-helper.ts getUserCustomToken' },
			'error'
		);

		throw error;
	}

	const customToken = response?.data?.customTokenFromBearerToken?.customToken as string | undefined;

	if (customToken) {
		try {
			const auth = getAuth();
			const userCredential = await signInWithCustomToken(auth, customToken);

			return userCredential.user;
		} catch (error: any) {
			const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
			captureMessageForSentry(
				'[user-helper.ts] signInWithCustomToken failed',
				{
					error,
					errorCode: error?.code,
					errorMessage: error?.message,
					where: 'user-helper.ts getUserCustomToken',
				},
				'error'
			);

			// Re-throw so caller can handle it (e.g., retry logic in main.common.ts)
			throw error;
		}
	}
	return undefined;
}

export async function generateToken(accessToken?: string, jwId?: string) {
	try {
		const user = await currentUser();

		if (!user && accessToken && jwId) {
			const customUser = await getUserCustomToken(accessToken, jwId);

			if (customUser) {
				return await customUser.getIdToken();
			}
		}

		if (!user) {
			const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
			captureMessageForSentry(
				'[user-helper.ts] Error getting firebase user',
				{ where: 'user-helper.ts generateToken' },
				'error'
			);

			throw new Error('Error getting firebase user');
		}

		const token = await user.getIdToken();
		return token;
	} catch (error: any) {
		const { captureMessageForSentry } = await import('@/helpers/sentry-helper');
		captureMessageForSentry(
			'[user-helper.ts] Error getting user custom token',
			{ error, where: 'user-helper.ts generateToken' },
			'error'
		);
	}
}

export async function reauthenticateUserWithPassword(password: string) {
	try {
		const user = await currentUser();
		if (!user) return false;
		const credential = EmailAuthProvider.credential(user.email, password);
		await reauthenticateWithCredential(user, credential);
		return true;
	} catch (error) {
		return false;
	}
}
